import { useState } from "react";
import { createFileRoute, redirect, useRouter } from "@tanstack/react-router";
import { ApiError } from "@/lib/api";
import { meQuery } from "@/lib/queries";
import { useLogin } from "@/lib/mutations";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const Route = createFileRoute("/login")({
  component: LoginPage,
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQuery);
    if (me.user) {
      throw redirect({ to: "/profile" });
    }
  },
});

function LoginPage() {
  const router = useRouter();
  const login = useLogin();
  const [email, setEmail] = useState("");

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim() || login.isPending) return;
    try {
      await login.mutateAsync(email.trim());
      await router.navigate({ to: "/profile" });
    } catch {
      // surfaced via login.error below
    }
  }

  const errorMessage =
    login.error instanceof ApiError
      ? login.error.message
      : login.error
      ? "Something went sideways. Try again."
      : null;

  return (
    <section className="mx-auto mt-12 max-w-md">
      <Card>
        <CardHeader>
          <CardTitle>Sign in</CardTitle>
          <p className="font-mono text-xs text-muted-foreground">
            Drop your email. We don't do passwords.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4" noValidate>
            <div className="space-y-1">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                inputMode="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
              />
            </div>

            {errorMessage && (
              <div
                role="alert"
                className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 font-mono text-sm text-destructive"
              >
                {errorMessage}
              </div>
            )}

            <Button
              type="submit"
              size="lg"
              className="w-full"
              disabled={login.isPending}
            >
              {login.isPending ? "Sending…" : "Send me in"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </section>
  );
}
